<script lang="ts">
    import { injectWiring } from '@tilelist/application/wiring/TileListWiringContext.svelte.ts';
    import BenefitCinema from '@tilelist/components/benefit-cinema/BenefitCinema.svelte';
    import BlackFridaySlider from '@tilelist/components/black-friday-slider/BlackFridaySlider.svelte';
    import BrandTile from '@tilelist/components/brand-tile/BrandTile.svelte';
    import ProductComparisonBanner from '@tilelist/components/product-comparison/ProductComparisonBanner.svelte';
    import ProductTileItem from '@tilelist/components/product-tile-item/ProductTileItem.svelte';
    import SdaHoarding from '@tilelist/components/sda-hoarding/SdaHoarding.svelte';
    import SimilarProductHeader from '@tilelist/components/tile-items/similar-product-header/SimilarProductHeader.svelte';
    import Slider from '@tilelist/components/tile-items/Slider.svelte';
    import { isBlackFridaySliderItem, isProductItem } from '@tilelist/domain/composition/TileListitem.ts';
    import type { ProductItem, TileItem, TileListItem } from '@tilelist/domain/composition/TileListitem.ts';
    import type { TileListPageContext } from '@tilelist/domain/composition/TileListPage.ts';
    import { computeFeatureIndices } from '@tilelist/domain/external-data-attributes/ExternalDataAttributes.ts';
    import type { FeatureIndexOffset } from '@tilelist/domain/external-data-attributes/ExternalDataAttributes.ts';
    import { shouldShowInstallments } from '@tilelist/domain/feature-visibility/InstallmentVisibility';
    import { shouldShowMultipleSizes } from '@tilelist/domain/feature-visibility/TopInfoVisibility';
    import ProductGridDispatcher from '@tilelist/layouts/dispatchers/product-grid-dispatcher/ProductGridDispatcher.svelte';

    type Props = {
        tileListItems: TileListItem[];
        context: TileListPageContext;
        showTopInfos?: boolean;
        cacheRestored?: boolean;
        onRedirectToPdp?: (item: TileItem) => void;
        onProductItemUpdated?: (item: ProductItem) => void;
        onSliderLoad?: () => void;
        featureIndexOffset: FeatureIndexOffset;
    };

    let {
        tileListItems: externalTileList,
        context,
        showTopInfos = false,
        cacheRestored = true,
        onRedirectToPdp = () => {},
        onProductItemUpdated = () => {},
        onSliderLoad = () => {},
        featureIndexOffset,
    }: Props = $props();

    const wiring = injectWiring(['controller']);
    const controller = $derived(wiring.controller);

    const onex = wiring.onex;
    const showSdaHoarding = $derived(!onex.isToggleActive('EMERGENCY_DISABLE_SDA_HOARDING'));
    const showBenefitCinema = $derived(!onex.isToggleActive('EMERGENCY_DISABLE_BENEFIT_CINEMA'));

    const tileListItems = $derived.by(() => {
        const _ = $state(externalTileList);
        return _;
    });

    /**
 *
 *
 *
 *
 *
 */
    const listItemKey = (item: TileListItem, i: number) => {
        if (isBlackFridaySliderItem(item)) return `${item.type}.${i}.${context.rule}`;
        if (!isProductItem(item)) return `${item.type}.${i}`;
        return `${item.type}.${item.localListPosition}.${item.product.id}`;
    };

    const columns = $derived(context?.layout?.columns);

    const showInstallments = $derived(shouldShowInstallments(context.searchContext));
    const showMultipleSizes = $derived(shouldShowMultipleSizes(context.searchContext));
    const showRadioEquipmentRegulation = $derived(
        onex.isToggleActive('ENABLE_FEATURE_FUAG_COMPONENT_ON_TILE') ?? false,
    );

    const featureIndices = $derived(
        computeFeatureIndices(tileListItems, featureIndexOffset, showBenefitCinema, showSdaHoarding),
    );
</script>

<ProductGridDispatcher {columns}>
    {#each tileListItems as item, i (listItemKey(item, i))}
        {#if isProductItem(item)}
            <ProductTileItem
                {cacheRestored}
                productItem={item}
                rule={context.rule}
                layout={context.layout}
                loadVariation={async (variationId) => {
                    const updated = await controller.loadVariation(item, variationId);
                    if (!updated) return;
                    tileListItems[i] = updated;
                    onProductItemUpdated(updated);
                }}
                onSeen={async () => {
                    controller.trackVisibility(item);
                }}
                onTileClick={(event) => {
                    controller.trackClick(event, item, context.rule);
                    onRedirectToPdp(item);
                }}
                {showRadioEquipmentRegulation}
                {showTopInfos}
                {showInstallments}
                {showMultipleSizes}
                searchContext={context.searchContext}
            />
        {:else if item.type === 'SPH'}
            <SimilarProductHeader />
        {:else if item.type === 'BrandTile' && showSdaHoarding}
            <BrandTile
                {cacheRestored}
                campaign={item.campaign}
                localListPosition={item.localListPosition}
                actualListPosition={item.actualListPosition}
                featureIndex={featureIndices.get(i)?.featureIndex}
                rule={context.rule}
                onSeen={async () => {
                    controller.trackVisibility(item);
                }}
                onTileClick={() => {
                    controller.trackBrandTileClick(item.actualListPosition, context.rule);
                    onRedirectToPdp(item);
                }}
            />
        {:else if isBlackFridaySliderItem(item)}
            <BlackFridaySlider
                slider={item}
                {context}
                {cacheRestored}
                {showRadioEquipmentRegulation}
                onLoad={onSliderLoad}
                onAction={(action) => controller.trackBlackFridaySliderAction(action)}
                onProductClick={(event, product) => {
                    controller.trackBlackFridaySliderClick(event, product, context.rule);
                    onRedirectToPdp(product);
                }}
                onProductSeen={(product) => controller.trackBlackFridaySliderTileVisibility(product)}
            />
        {:else if item.type === 'SLIDER'}
            <Slider {...item} />
        {:else if item.type === 'PCB'}
            <ProductComparisonBanner />
        {:else if item.type === 'SponsoredDisplayAd' && showSdaHoarding}
            <SdaHoarding
                listSlot="list-slot"
                searchContext={item.searchContext}
                featureIndex={featureIndices.get(i)?.featureIndex}
                hidden={item.hidden}
            />
        {:else if item.type === 'BenefitCinema' && showBenefitCinema}
            <BenefitCinema
                dreson={context.rule}
                position="list"
                tilelistSlotIndex={featureIndices.get(i)?.tilelistSlotIndex}
                featureIndex={featureIndices.get(i)?.featureIndex}
            />
        {/if}
    {/each}
</ProductGridDispatcher>
